import DOMPurify from 'dompurify'
import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Balance, RateReview } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, Link, MenuItem, Rating, Stack, TextField, Typography } from '@mui/material'

import { GameIcon } from '../../../../../features/game'
import { gameSaasPath } from '../../../games/components/GameNameCell/GameNameCell'
import { AnalysisSuggestionRow, AnalysisSuggestionType, AnalystOption } from '../../types/AnalysisSuggestion'

type AnalysisSuggestionCardProps = {
  row: AnalysisSuggestionRow
  analysts: AnalystOption[]
  /** Disables the controls while a change is saved. */
  saving?: boolean
  onAssign: (row: AnalysisSuggestionRow, email: string) => void
  onCompletedChange: (row: AnalysisSuggestionRow, completed: boolean) => void
  onComment: (row: AnalysisSuggestionRow) => void
  onViewAnalyses: (row: AnalysisSuggestionRow) => void
}

export const formatSuggestionDate = (timestamp: number) =>
  new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(',', '')

export const AnalysisSuggestionCard: FC<AnalysisSuggestionCardProps> = ({ row, analysts, saving, onAssign, onCompletedChange, onComment, onViewAnalyses }) => {
  const { t } = useTranslation()
  const { suggestion, game, type } = row
  const gameName = game?.name || t('internal-analysts:analysis_suggestions_unknown_game', { id: suggestion.gameId })
  const comment = useMemo(() => (suggestion.internalComment ? DOMPurify.sanitize(suggestion.internalComment) : ''), [suggestion.internalComment])
  const assigned = suggestion.assignedAnalyst ?? ''
  const formerAnalyst = !!assigned && !analysts.some(({ email }) => email === assigned)

  return (
    <Card component="article" aria-label={gameName} variant="outlined">
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} spacing={2}>
          {type ? (
            <Chip
              size="small"
              color={type === AnalysisSuggestionType.New ? 'success' : 'info'}
              label={t(
                type === AnalysisSuggestionType.New ? 'internal-analysts:analysis_suggestions_type_new' : 'internal-analysts:analysis_suggestions_type_update'
              )}
            />
          ) : (
            <Box />
          )}
          <Typography variant="body2" color="text.secondary">
            {formatSuggestionDate(suggestion.createdAt)}
          </Typography>
        </Stack>
        <Box
          display="grid"
          gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr', lg: 'minmax(220px, 1.2fr) minmax(260px, 2fr) minmax(200px, 1fr) minmax(240px, auto)' }}
          gap={2}
        >
          <Stack direction="row" spacing={1.5} minWidth={0}>
            {game && <GameIcon src={game.icons?.[0] ?? game.icon ?? ''} gameName={game.name} size="small" />}
            <Box minWidth={0}>
              {game ? (
                <Link href={gameSaasPath(game.id)} target="_blank" rel="noopener noreferrer" fontWeight="bold" underline="hover">
                  {gameName}
                </Link>
              ) : (
                <Typography fontWeight="bold">{gameName}</Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {game?.artist || '---'}
              </Typography>
              <Typography variant="body2">{t('internal-analysts:analysis_suggestions_version', { version: suggestion.gameVersion || '---' })}</Typography>
            </Box>
          </Stack>

          <Box minWidth={0}>
            <Typography variant="body2" color="text.secondary">
              {t('internal-analysts:analysis_suggestions_user_comment')}
            </Typography>
            <Typography whiteSpace="pre-wrap" sx={{ overflowWrap: 'anywhere' }}>
              {suggestion.requestReason || '---'}
            </Typography>
            {comment && (
              <Box mt={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {t('internal-analysts:analysis_suggestions_internal_comment')}
                </Typography>
                <Box
                  data-testid="analysis-suggestion-internal-comment"
                  sx={{ overflowWrap: 'anywhere', '& > :first-of-type': { mt: 0 }, '& > :last-child': { mb: 0 } }}
                  dangerouslySetInnerHTML={{ __html: comment }}
                />
              </Box>
            )}
          </Box>

          <Box minWidth={0}>
            <Typography fontWeight="bold">{row.organizationName ?? t('internal-analysts:analysis_suggestions_unknown_organization')}</Typography>
            <Typography>{row.userName ?? '---'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {suggestion.requestingUser}
            </Typography>
            <Rating
              value={suggestion.importance}
              readOnly
              size="small"
              aria-label={t('internal-analysts:analysis_suggestions_importance_value', { importance: suggestion.importance })}
            />
          </Box>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1}>
              <Button size="small" variant="outlined" startIcon={<Balance />} disabled={!game} onClick={() => onViewAnalyses(row)}>
                {t('internal-analysts:analysis_suggestions_view_analyses')}
              </Button>
              <Button size="small" variant="outlined" startIcon={<RateReview />} disabled={saving} onClick={() => onComment(row)}>
                {t('internal-analysts:analysis_suggestions_comment')}
              </Button>
            </Stack>
            <TextField
              select
              size="small"
              label={t('internal-analysts:analysis_suggestions_assigned')}
              value={assigned}
              disabled={saving}
              SelectProps={{ displayEmpty: true }}
              InputLabelProps={{ shrink: true }}
              onChange={(event) => onAssign(row, event.target.value)}
            >
              <MenuItem value="" disabled>
                {t('internal-analysts:analysis_suggestions_not_assigned')}
              </MenuItem>
              {formerAnalyst && (
                <MenuItem value={assigned} disabled>
                  {assigned}
                </MenuItem>
              )}
              {analysts.map(({ email, name }) => (
                <MenuItem key={email} value={email}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={<Checkbox checked={suggestion.completed} disabled={saving} onChange={(event) => onCompletedChange(row, event.target.checked)} />}
              label={t('internal-analysts:analysis_suggestions_completed')}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  )
}
